import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useNavigate } from 'react-router-dom';

import { Box, Grid, Stack, Typography } from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import moment from 'moment';

import AddProductsDialog from '../../../../common/warehouse_and_food_partner_order_form/add_products_dialog';
import useNotification from '../../../../../../core/hooks/use_notification';
import { GET_ROUTE_FOOD_PARTNER_ORDER, MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_ISO_FORMAT, ROUTE_FOOD_PARTNER_ORDERS } from '../../../../../../core/constants';
import { generateFoodPartnerOrderDocumentsNotificationData, getFoodPartnerOrderNotificationData, updateFoodPartnerOrderNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import ProductsList from '../common/products_list';
import { getFoodPartnerOrderAction, getFoodPartnerOrderState } from '../../../../../../features/inventory/get_food_partner_order';
import { FormRouteEnum } from '../../../../../../core/enums/common/route';
import { FoodPartnerOrderStatusEnum } from '../../../../../../core/enums/inventory/food_partner_order';
import { SelectFieldFormik } from '../../../../../common/select_field';
import { DateTimePickerFormik } from '../../../../../common/date_picker';
import RadioGroupFormik from '../../../../../common/radio_group';
import { yesNoOptions } from '../../../../../common/form_utils';
import { updateFoodPartnerOrderAction, updateFoodPartnerOrderState } from '../../../../../../features/inventory/update_food_partner_order';
import AdditionalOptions from '../common/additional_options';
import { updateAndSendFoodPartnerOrderAction, updateAndSendFoodPartnerOrderState } from '../../../../../../features/inventory/update_and_send_food_partner_order';
import UploadFiles from '../../../../common/warehouse_and_food_partner_order_form/upload_files';
import WarehouseDocumentList from './warehouse_document_list';
import TextFieldFormik from '../../../../../common/text_field';
import { finalizeFoodPartnerOrderAction, finalizeFoodPartnerOrderState } from '../../../../../../features/inventory/finalize_food_partner_order';
import useUnsavedChangesWarning from '../../../../../layout/component/use_unsaved_changes_warning';
import SectionPaper from '../../../../../common/section_paper';
import BottomActionBar from '../../../../../common/bottom_action_bar';
import { Button } from '../../../../../common/button';
import ConfirmDialog from '../../../../common/confirm_dialog';
import BlockerDialog from '../../../../common/blocker_dialog';
import BlockerReturnButton from '../../../../common/blocker_return_button';
import { useDirtyForm } from '../../../../../../core/context/dirty_form_context';

const FormBody = ({
                      disableForm,
                      foodPartnerOrderStatus,
                      getFoodPartnerOrder,
                      updateFoodPartnerOrder,
                      updateAndSendFoodPartnerOrder,
                      finalizeFoodPartnerOrder,
                      onChangeSetDate,
                      onUpdate,
                      onGenerateWarehouseDocument
                  }) => {
    const navigate = useNavigate();
    const formik = useFormikContext();
    useUnsavedChangesWarning(formik.dirty);
    const { setIsDirty } = useDirtyForm();

    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        formik.dirty && currentLocation.pathname !== nextLocation.pathname
    );

    const isLoading = updateFoodPartnerOrder?.loading || updateAndSendFoodPartnerOrder?.loading || finalizeFoodPartnerOrder?.loading;
    const mailAlreadySent = getFoodPartnerOrder?.data?.emailStatus !== 'UNSENT';

    useEffect(() => {
        setIsDirty(formik.dirty);

        return () => setIsDirty(false);
    }, [formik.dirty, setIsDirty]);

    return (
        <>
            <BlockerDialog blocker={ blocker }/>
            <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                <Box sx={ { flexGrow: 1, pb: 4 } }>
                    <Stack spacing={ 3 }>
                        <SectionPaper title="Dane zamówienia">
                            <Grid container spacing={ 3 }>
                                <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                    <SelectFieldFormik
                                        name="foodPartnerId"
                                        label="Dostawca"
                                        options={ [{
                                            value: getFoodPartnerOrder?.data?.foodPartnerId,
                                            label: getFoodPartnerOrder?.data?.foodPartnerName
                                        }] }
                                        required
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                    <RadioGroupFormik
                                        name="locationDivision"
                                        label="Podział na lokalizacje"
                                        options={ yesNoOptions }
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                    <DateTimePickerFormik
                                        name="orderDate"
                                        label="Data zamówienia"
                                        required
                                        InputLabelProps={ { shrink: true } }
                                        disabled={ ![FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) }
                                    />
                                </Grid>
                            </Grid>
                        </SectionPaper>

                        {
                            [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) &&
                            <AdditionalOptions
                                onChangeSetDate={ (newDate, allDates) => onChangeSetDate(newDate, allDates, formik) }
                                formik={ formik }/>
                        }

                        <SectionPaper title="Lista produktów">
                            <Stack spacing={ 2 }>
                                <ProductsList
                                    formik={ formik }
                                    productsListIndex={ 0 }
                                    withExpirationDate
                                    disabled={ disableForm || [FoodPartnerOrderStatusEnum.DELIVERED.value, FoodPartnerOrderStatusEnum.COMPLETED.value].includes(foodPartnerOrderStatus) }
                                />
                                {
                                    [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) &&
                                    <Box sx={ { mt: 2 } }>
                                        <AddProductsDialog
                                            foodPartnerId={ formik?.values?.foodPartnerId }
                                            formik={ formik }
                                            dateToCalculateExpirationDate={ formik?.values?.orderDate }
                                            productsListIndex={ 0 }
                                        />
                                    </Box>
                                }
                            </Stack>
                        </SectionPaper>

                        {
                            getFoodPartnerOrder?.data?.warehouseDocuments &&
                            <WarehouseDocumentList documents={ getFoodPartnerOrder?.data?.warehouseDocuments }/>
                        }

                        <SectionPaper>
                            <Stack spacing={ 3 }>
                                <UploadFiles
                                    formik={ formik }
                                    downloadOption
                                    addAndRemoveOptions={ [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) }
                                />
                                <Box>
                                    <Typography variant="subtitle2" sx={ {
                                        fontWeight: 600,
                                        mb: 1,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase'
                                    } }>
                                        Notatka do zamówienia
                                    </Typography>
                                    <TextFieldFormik
                                        name="orderNote"
                                        multiline
                                        rows={ 3 }
                                        disabled={ disableForm || [FoodPartnerOrderStatusEnum.DELIVERED.value, FoodPartnerOrderStatusEnum.COMPLETED.value].includes(foodPartnerOrderStatus) }
                                    />
                                </Box>
                            </Stack>
                        </SectionPaper>
                    </Stack>
                </Box>

                <BottomActionBar>
                    <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                        <BlockerReturnButton formik={ formik } onReturn={ () => navigate(ROUTE_FOOD_PARTNER_ORDERS) }/>

                        <Stack direction="row" spacing={ 2 }>
                            {
                                [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value, FoodPartnerOrderStatusEnum.DELIVERED.value].includes(foodPartnerOrderStatus) &&
                                <ConfirmDialog
                                    isLoading={ isLoading }
                                    title="Wygenerować paczkę naklejek?"
                                    secondaryAction={ () => onUpdate(formik, false, false) }
                                    secondaryText="Nie pobieraj"
                                    primaryAction={ () => onUpdate(formik, false, true) }
                                    primaryText="Pobierz"
                                    displayIcon={ false }
                                    buttonText="Zapisz"
                                />
                            }
                            {
                                [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) &&
                                <ConfirmDialog
                                    isLoading={ isLoading }
                                    maxWidth="md"
                                    title={ mailAlreadySent
                                        ? 'Czy na pewno chcesz ponownie wysłać maila do Dostawcy?'
                                        : 'Czy chcesz uwzględnić paczkę z naklejkami w mailu z zamówieniem?'
                                    }
                                    subtitle={ mailAlreadySent
                                        ? 'Pamiętaj, żeby wysyłać maila tylko przy aktualizacji zamówienia. Możesz uwzględnić paczkę z naklejkami w mailu z aktualizacją, lub wysłać ją bez naklejek.'
                                        : null
                                    }
                                    cancelText="Anuluj"
                                    secondaryAction={ () => onUpdate(formik, true, false) }
                                    secondaryText="Zapisz i wyślij bez naklejek"
                                    primaryAction={ () => onUpdate(formik, true, true) }
                                    primaryText="Zapisz i wyślij z naklejkami"
                                    displayIcon={ false }
                                    buttonText="Zapisz i wyślij"
                                />
                            }
                            {
                                foodPartnerOrderStatus === FoodPartnerOrderStatusEnum.DELIVERED.value &&
                                <Button
                                    onClick={ () => onGenerateWarehouseDocument(formik) }
                                    isLoading={ isLoading }
                                >
                                    Przyjmij na magazyn
                                </Button>
                            }
                        </Stack>
                    </Box>
                </BottomActionBar>
            </Box>
        </>
    );
};

const FormWithoutLocationDivision = ({ action, setAction }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { showErrorMessage, showNotification } = useNotification();

    const getFoodPartnerOrder = useSelector(getFoodPartnerOrderState);
    const updateFoodPartnerOrder = useSelector(updateFoodPartnerOrderState);
    const updateAndSendFoodPartnerOrder = useSelector(updateAndSendFoodPartnerOrderState);
    const finalizeFoodPartnerOrder = useSelector(finalizeFoodPartnerOrderState);

    const disableForm = action === FormRouteEnum.VIEW;
    const foodPartnerOrderStatus = getFoodPartnerOrder?.data?.status;

    const initialValues = {
        foodPartnerId: getFoodPartnerOrder?.data?.foodPartnerId || '',
        locationDivision: false,
        orderDate: moment(getFoodPartnerOrder?.data?.orderDate) || '',
        productsList: [getFoodPartnerOrder?.data?.positions?.map((position, index) => ({
            position: index,
            name: position?.productName,
            productId: position?.productId,
            quantity: position?.quantity,
            expirationDate: moment(position?.expirationDate) || '',
            price: position?.purchaseNetPrice ? Number(position.purchaseNetPrice).toFixed(2) : ''
        }))] || [],
        attachments: getFoodPartnerOrder?.data?.attachments || [],
        attachmentIdsToDelete: [],
        orderNote: getFoodPartnerOrder?.data?.orderNote || '',
    };

    const onChangeSetDate = (newDate, allDates, formik) => {
        const productsList = formik?.values?.productsList[0]?.map(item => {
            if (!allDates && item?.expirationDate) {
                return item;
            }

            return ({ ...item, expirationDate: newDate });
        });

        formik.setFieldValue(`productsList.${ 0 }`, productsList);
    };

    const validateOnSubmit = async (formik) => {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            const touched = {
                productsList: formik.values.productsList.map(list =>
                    list ? list.map(() => ({
                        quantity: true,
                        price: true,
                        expirationDate: true
                    })) : []
                )
            };
            formik.setTouched(touched);
            return false;
        }
        return true;
    };

    const onUpdate = async (formik, sendEmail, generateLabels) => {
        const isValid = await validateOnSubmit(formik);
        if (!isValid) return;

        const locationProducts = [{
            products: [
                ...formik?.values?.productsList?.[0]?.map(item => ({
                    productId: item?.productId,
                    quantity: item?.quantity,
                    price: item?.price,
                    expirationDate: item?.expirationDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
                }))
            ]
        }];
        const form = {
            locationProducts,
            generateLabels,
            orderDate: formik?.values?.orderDate?.format(MISC_DATE_ISO_FORMAT),
            attachmentIdsToDelete: formik?.values?.attachmentIdsToDelete,
            orderNote: formik?.values?.orderNote,
        };
        const attachments = formik?.values?.attachments || [];
        const dispatchAction = sendEmail ? updateAndSendFoodPartnerOrderAction : updateFoodPartnerOrderAction;

        dispatch(dispatchAction({ id: getFoodPartnerOrder?.data?.id, form, attachments }))
            .then(response => {
                showNotification(updateFoodPartnerOrderNotificationData(response));

                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    dispatch(getFoodPartnerOrderAction(getFoodPartnerOrder?.data?.id))
                        .then(response => showErrorMessage(getFoodPartnerOrderNotificationData(response)));
                }
            });
    };

    const onGenerateWarehouseDocument = async (formik) => {
        const isValid = await validateOnSubmit(formik);
        if (!isValid) return;

        dispatch(finalizeFoodPartnerOrderAction(getFoodPartnerOrder?.data?.id))
            .then(response => {
                showNotification(generateFoodPartnerOrderDocumentsNotificationData(response));

                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    setAction(FormRouteEnum.VIEW);
                    navigate(GET_ROUTE_FOOD_PARTNER_ORDER(getFoodPartnerOrder?.data?.id));
                }
            });
    };

    const validate = (values) => {
        const errors = {};
        const productsListErrors = [];
        let hasErrors = false;

        if (!values.productsList || values.productsList.length === 0) {
            errors.productsList = ['Wybierz minimum 1 produkt'];
            return errors;
        }

        values.productsList.forEach((products, index) => {
            if (!products || products.length === 0) {
                productsListErrors[index] = 'Wybierz minimum 1 produkt';
                hasErrors = true;
            } else {
                const itemErrorsArray = [];
                products.forEach((item, itemIndex) => {
                    const itemErrors = {};

                    if (!item?.quantity) {
                        itemErrors.quantity = 'Pole wymagane';
                    } else if (item?.quantity <= 0) {
                        itemErrors.quantity = 'Proszę podać wartość całkowitą dodatnią';
                    }

                    if (item?.price && item?.price <= 0) {
                        itemErrors.price = 'Proszę podać wartość dodatnią';
                    }

                    if (!item?.expirationDate) {
                        itemErrors.expirationDate = 'Pole wymagane';
                    }

                    if (Object.keys(itemErrors).length > 0) {
                        itemErrorsArray[itemIndex] = itemErrors;
                        hasErrors = true;
                    }
                });

                if (itemErrorsArray.length > 0 || productsListErrors[index]) {
                    productsListErrors[index] = productsListErrors[index] || itemErrorsArray;
                }
            }
        });

        if (hasErrors) {
            errors.productsList = productsListErrors;
        }

        return errors;
    };

    return (
        <Formik initialValues={ initialValues } enableReinitialize={ true } validate={ validate }>
            <FormBody
                disableForm={ disableForm }
                foodPartnerOrderStatus={ foodPartnerOrderStatus }
                getFoodPartnerOrder={ getFoodPartnerOrder }
                updateFoodPartnerOrder={ updateFoodPartnerOrder }
                updateAndSendFoodPartnerOrder={ updateAndSendFoodPartnerOrder }
                finalizeFoodPartnerOrder={ finalizeFoodPartnerOrder }
                onChangeSetDate={ onChangeSetDate }
                onUpdate={ onUpdate }
                onGenerateWarehouseDocument={ onGenerateWarehouseDocument }
            />
        </Formik>
    );
};

export default FormWithoutLocationDivision;